import React, { useState } from 'react';
import { Ruler, ChevronDown, ChevronUp, Info } from 'lucide-react';

const GEAR_CATEGORIES = [
  {
    id: 'helmets',
    name: 'Helmets',
    icon: null,
    description: 'Measure around the widest part of your head, about 1 inch above your eyebrows.',
    howTo: 'Wrap a soft measuring tape around the largest part of your head — just above the eyebrows, across the forehead, and around the back of the skull. Pull snug but not tight.',
    sizes: [
      { us: 'XS', eu: 'XS', cm: '53-54', inches: '20.9-21.3' },
      { us: 'S', eu: 'S', cm: '55-56', inches: '21.7-22.0' },
      { us: 'M', eu: 'M', cm: '57-58', inches: '22.4-22.8' },
      { us: 'L', eu: 'L', cm: '59-60', inches: '23.2-23.6' },
      { us: 'XL', eu: 'XL', cm: '61-62', inches: '24.0-24.4' },
      { us: '2XL', eu: '2XL', cm: '63-64', inches: '24.8-25.2' },
    ],
    measurement: 'Head Circumference',
    tips: [
      'A properly fitted helmet should feel snug with no pressure points',
      'The helmet should not rotate freely on your head',
      'Cheek pads should touch your cheeks without uncomfortable pressure',
      'If you are between sizes, go with the larger size',
    ],
    kits: ['Black Out Edition Kit', 'Summer Commuter Kit', 'Adventure Explorer Kit', 'Gotham Air Pro', 'Track Pro Kit', 'X-Plorer Pro'],
  },
  {
    id: 'jackets',
    name: 'Jackets',
    icon: null,
    description: 'Measure your chest at its widest point and your natural waist.',
    howTo: 'Chest: Stand with arms relaxed at your sides. Wrap measuring tape around the fullest part of your chest, under your armpits and across your shoulder blades. Waist: Measure around your natural waistline, about 1 inch above your belly button.',
    sizes: [
      { us: 'S', eu: '46', chest_cm: '88-92', chest_in: '34.5-36', waist_cm: '76-80', waist_in: '30-31.5' },
      { us: 'M', eu: '48-50', chest_cm: '93-100', chest_in: '36.5-39.5', waist_cm: '81-88', waist_in: '32-34.5' },
      { us: 'L', eu: '52-54', chest_cm: '101-108', chest_in: '40-42.5', waist_cm: '89-96', waist_in: '35-38' },
      { us: 'XL', eu: '56-58', chest_cm: '109-116', chest_in: '43-45.5', waist_cm: '97-104', waist_in: '38-41' },
      { us: '2XL', eu: '60-62', chest_cm: '117-124', chest_in: '46-49', waist_cm: '105-112', waist_in: '41.5-44' },
    ],
    measurement: 'Chest / Waist',
    tips: [
      'Motorcycle jackets should fit snugly — armor shifts during impact if the jacket is too loose',
      'You should be able to reach forward (riding position) without the jacket pulling up',
      'Sleeve length: wrists should be covered when arms are extended forward on handlebars',
      'Zip the jacket fully and check that armor sits over your shoulders, elbows, and back correctly',
    ],
    kits: ['Black Out Edition Kit', 'Summer Commuter Kit', 'Adventure Explorer Kit', 'Gotham Air Pro', 'Track Pro Kit', 'X-Plorer Pro'],
  },
  {
    id: 'gloves',
    name: 'Gloves',
    icon: null,
    description: 'Measure around the widest part of your palm, excluding thumb.',
    howTo: 'Wrap a tape measure around your dominant hand at the widest part of the palm (just below the knuckles), excluding the thumb. Make a loose fist while measuring.',
    sizes: [
      { us: 'S', eu: '7-7.5', cm: '18-19', inches: '7.0-7.5' },
      { us: 'M', eu: '8-8.5', cm: '20-21.5', inches: '7.9-8.5' },
      { us: 'L', eu: '9-9.5', cm: '22-23', inches: '8.7-9.1' },
      { us: 'XL', eu: '10-10.5', cm: '24-25', inches: '9.4-9.8' },
      { us: '2XL', eu: '11-11.5', cm: '26-27', inches: '10.2-10.6' },
    ],
    measurement: 'Palm Circumference',
    tips: [
      'Gloves should fit snugly but allow full finger movement and grip',
      'Fingertips should reach the end of the glove without excess material',
      'New leather gloves will stretch slightly after break-in (1-2 rides)',
      'You should be able to operate all controls (throttle, brake, clutch) comfortably',
    ],
    kits: ['Black Out Edition Kit', 'Adventure Explorer Kit', 'Track Pro Kit', 'X-Plorer Pro'],
  },
  {
    id: 'boots',
    name: 'Boots',
    icon: null,
    description: 'Measure your foot length from heel to longest toe.',
    howTo: 'Stand on a piece of paper with your heel against a wall. Mark the tip of your longest toe. Measure the distance from the wall to the mark. Measure both feet — use the larger measurement.',
    sizes: [
      { us: '7', eu: '40', cm: '25.0', inches: '9.8' },
      { us: '8', eu: '41', cm: '26.0', inches: '10.2' },
      { us: '9', eu: '42', cm: '27.0', inches: '10.6' },
      { us: '9.5', eu: '43', cm: '27.5', inches: '10.8' },
      { us: '10', eu: '44', cm: '28.0', inches: '11.0' },
      { us: '10.5', eu: '44.5', cm: '28.5', inches: '11.2' },
      { us: '11', eu: '45', cm: '29.0', inches: '11.4' },
      { us: '12', eu: '46', cm: '30.0', inches: '11.8' },
      { us: '13', eu: '47', cm: '31.0', inches: '12.2' },
    ],
    measurement: 'Foot Length',
    tips: [
      'Motorcycle boots should fit snugly around the ankle for protection',
      'Your toes should lightly touch the front but not be cramped',
      'Wear the same socks you ride in when trying boots',
      'Racing boots fit tighter than touring/adventure boots — account for your riding style',
    ],
    kits: ['Adventure Explorer Kit', 'Track Pro Kit', 'X-Plorer Pro'],
  },
  {
    id: 'pants',
    name: 'Pants',
    icon: null,
    description: 'Measure your waist and inseam for the best fit.',
    howTo: 'Waist: Measure around your natural waistline. Inseam: Measure from the crotch seam to the bottom of the ankle bone on the inside of the leg. Stand straight with feet slightly apart.',
    sizes: [
      { us: 'S (30-32)', eu: '46-48', waist_cm: '76-84', waist_in: '30-33', inseam_cm: '78-80', inseam_in: '30.5-31.5' },
      { us: 'M (33-34)', eu: '50-52', waist_cm: '85-92', waist_in: '33.5-36', inseam_cm: '80-82', inseam_in: '31.5-32.5' },
      { us: 'L (35-36)', eu: '54-56', waist_cm: '93-100', waist_in: '36.5-39.5', inseam_cm: '82-84', inseam_in: '32.5-33' },
      { us: 'XL (38-40)', eu: '58-60', waist_cm: '101-108', waist_in: '40-42.5', inseam_cm: '84-86', inseam_in: '33-34' },
      { us: '2XL (42-44)', eu: '62-64', waist_cm: '109-116', waist_in: '43-45.5', inseam_cm: '86-88', inseam_in: '34-34.5' },
    ],
    measurement: 'Waist / Inseam',
    tips: [
      'Riding pants should allow a slight bend at the knee while standing',
      'Knee armor should sit directly over your kneecap when in riding position',
      'Hip armor should cover the hip bone — check in a seated position',
      'Pants should be long enough that they don\'t ride up above your boots when seated on the bike',
    ],
    kits: ['Adventure Explorer Kit', 'Track Pro Kit'],
  },
];

const SizeChart = ({ category }) => {
  const isJacket = category.id === 'jackets';
  const isPants = category.id === 'pants';

  return (
    <div className="overflow-x-auto" data-testid={`size-chart-${category.id}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#1a1a1a]">
            <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">US Size</th>
            <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">EU Size</th>
            {isJacket ? (
              <>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Chest (cm)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Chest (in)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Waist (cm)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Waist (in)</th>
              </>
            ) : isPants ? (
              <>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Waist (cm)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Waist (in)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Inseam (cm)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">Inseam (in)</th>
              </>
            ) : (
              <>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">{category.measurement} (cm)</th>
                <th className="text-left px-4 py-3 text-[#C6A87C] uppercase text-xs tracking-wider font-semibold">{category.measurement} (in)</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {category.sizes.map((size, idx) => (
            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 font-semibold" style={{ color: '#333' }}>{size.us}</td>
              <td className="px-4 py-3" style={{ color: '#666' }}>{size.eu}</td>
              {isJacket ? (
                <>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.chest_cm}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.chest_in}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.waist_cm}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.waist_in}</td>
                </>
              ) : isPants ? (
                <>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.waist_cm}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.waist_in}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.inseam_cm}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.inseam_in}</td>
                </>
              ) : (
                <>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.cm}</td>
                  <td className="px-4 py-3" style={{ color: '#333' }}>{size.inches}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SizingGuidePage = () => {
  const [expandedCategory, setExpandedCategory] = useState('helmets');

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="sizing-guide-page">
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-[1000px] mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">Reference</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight" style={{ color: '#333333' }}>
              Sizing Guide
            </h1>
            <p className="mt-2 text-base" style={{ color: '#666666' }}>
              Find your perfect fit. Accurate sizing means better protection and comfort on every ride.
            </p>
          </div>

          {/* Quick tips */}
          <div className="bg-[#1a1a1a] p-6 mb-10">
            <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-[#C6A87C] mb-3 flex items-center gap-2">
              <Ruler className="w-5 h-5" /> How to Measure
            </h2>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-[#C6A87C] mt-0.5">1.</span>
                Use a soft fabric measuring tape — not a metal one
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C6A87C] mt-0.5">2.</span>
                Measure over light clothing (t-shirt), not over a jacket
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C6A87C] mt-0.5">3.</span>
                Keep the tape snug but not tight — you should be able to fit one finger underneath
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C6A87C] mt-0.5">4.</span>
                If between sizes, size up — it's safer to have a slightly looser fit than gear that restricts movement
              </li>
            </ul>
          </div>

          {/* Category sections */}
          <div className="space-y-4" data-testid="sizing-categories">
            {GEAR_CATEGORIES.map(category => {
              const isExpanded = expandedCategory === category.id;
              return (
                <div key={category.id} className="border border-gray-200 overflow-hidden" data-testid={`category-${category.id}`}>
                  {/* Category Header */}
                  <button
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors text-left"
                    data-testid={`category-toggle-${category.id}`}
                  >
                    <div>
                      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight" style={{ color: '#333' }}>
                        {category.name}
                      </h2>
                      <p className="text-sm mt-0.5" style={{ color: '#999' }}>{category.description}</p>
                    </div>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-[#C6A87C]" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-200">
                      {/* How to measure */}
                      <div className="p-5 bg-[#C6A87C]/5 border-b border-gray-200">
                        <h3 className="text-xs uppercase tracking-widest font-semibold mb-2 flex items-center gap-1.5" style={{ color: '#C6A87C' }}>
                          <Info className="w-3.5 h-3.5" /> How to Measure
                        </h3>
                        <p className="text-sm" style={{ color: '#555' }}>{category.howTo}</p>
                      </div>

                      {/* Size Chart */}
                      <SizeChart category={category} />

                      {/* Fit Tips */}
                      <div className="p-5 bg-gray-50 border-t border-gray-200">
                        <h3 className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: '#C6A87C' }}>
                          Fit Tips
                        </h3>
                        <ul className="space-y-2">
                          {category.tips.map((tip, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#555' }}>
                              <span className="w-1.5 h-1.5 rounded-full bg-[#C6A87C] mt-1.5 flex-shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Included in kits */}
                      <div className="p-5 border-t border-gray-200">
                        <h3 className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: '#999' }}>
                          Included in
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {category.kits.map(kit => (
                            <span key={kit} className="text-xs px-3 py-1 bg-gray-100 border border-gray-200" style={{ color: '#666' }}>
                              {kit}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Need help? */}
          <div className="mt-10 p-6 bg-gray-50 border border-gray-200 text-center">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-2" style={{ color: '#333' }}>Not Sure About Your Size?</h3>
            <p className="text-sm mb-4" style={{ color: '#666' }}>
              Ask Clutch, our AI assistant, for help — or reach out to us at support@mygearrental.com. 
              We're happy to help you find the perfect fit.
            </p>
            <p className="text-xs" style={{ color: '#999' }}>
              All measurements are approximate. Sizing may vary slightly between manufacturers. When in doubt, size up for comfort and safety.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SizingGuidePage;
